/**
 * @file index.tsx
 *
 * @fileoverview Renders the job form.
 */
import React, { useContext } from 'react';
import ADD_JOB_FILE from '~/jobiqo/components/Job/Wizard/graphql/mutations/addJobPerFile.gql';
import ADD_JOB_LINK from '~/jobiqo/components/Job/Wizard/graphql/mutations/addJobPerLink.gql';
import ADD_JOB_TEMPLATE from '~/jobiqo/components/Job/Wizard/graphql/mutations/addJobPerTemplate.gql';
import { useMutation } from '@apollo/client';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { ContentErrorList } from '~/jobiqo/components/ContentErrorList';
import { getAnonymousUserToken } from '~/jobiqo/lib/session';
import Router from 'next/router';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { MessageService, useMessages } from '~/jobiqo/hooks/messages';
import { getAPIEntityValues } from '~/jobiqo/utils/config';
import { FieldGroupInfo } from '~/jobiqo/models/forms';
import { User } from '~/jobiqo/models/user';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import JobWizardStepFormView from './view';
import { ROLE_RECRUITER } from '~/jobiqo/lib/auth';
import JobTypeEnum from '~/jobiqo/enums/jobType';
import { useRuntimeStepsInfo } from '~/jobiqo/components/Job/Wizard/hooks/useRuntimeStepsInfo';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { useJobWizardContext } from '~/jobiqo/components/Job/Wizard/context/useJobWizardContext';
import { Product } from '~/jobiqo/types/types';
import {
  getCurrentBundle,
  handleCheckout,
} from '~/jobiqo/components/Job/Wizard/utils';
import JOB_CHECKOUT_ORDER from '~/jobiqo/components/Job/Wizard/graphql/mutations/jobCheckoutOrder.gql';
import { useBundleContainsAllUpgrades } from '~/jobiqo/hooks/useBundleContainsAllUpgrades';
import { useDefaultProductBundle } from '~/jobiqo/components/Job/Wizard/hooks';

/**
 * Get the appropriate mutation depending on the url
 * type prop.
 *
 * @param {string} jobType
 *  The type of job being inserted
 */
const getMutation = (jobType: string) => {
  switch (jobType) {
    case JobTypeEnum.JOB_PER_TEMPLATE:
      return ADD_JOB_TEMPLATE;
    case JobTypeEnum.JOB_PER_LINK:
      return ADD_JOB_LINK;
    case JobTypeEnum.JOB_PER_FILE:
      return ADD_JOB_FILE;
  }
};

const handleJobPostingComplete = (
  response: any,
  nextStep: string,
  messagesService: MessageService,
  intl: IntlShape,
  jobId: number,
  querystring: any,
  isLastStep: boolean,
  checkout: any,
  user?: User,
  locale?: string,
  currentBundle?: Product,
) => {
  // Continue checkout process for non-anonymous users.
  if (response.data.addJob.job.ownerId !== 0) {
    if (user && user.isUserApprovedAndVerified && jobId) {
      const flow =
        querystring.edit === JobWizardFlow.Republish
          ? JobWizardFlow.Republish
          : querystring.edit === JobWizardFlow.Upgrades
          ? JobWizardFlow.Upgrades
          : JobWizardFlow.New;

      const upgradesToSend: number[] = [];

      // If bundle is selected make sure we are adding it to the order.
      if (currentBundle) {
        upgradesToSend.push(currentBundle.id);
      }

      handleCheckout(
        jobId,
        querystring,
        nextStep,
        isLastStep,
        checkout,
        flow,
        locale,
        upgradesToSend,
      );
    } else {
      Router.replace(`/job-manager`).then(() => {
        messagesService.addMessage({
          type: 'warning',
          text: intl.formatMessage({
            id: 'job_saved_account_verification_needed',
            defaultMessage:
              'Your job was saved, In order to publish it you need to verify your account first.',
          }),
        });
      });
    }
  } else {
    Router.replace(`/user/register/${ROLE_RECRUITER}`).then(() => {
      messagesService.addMessage({
        type: 'success',
        text: intl.formatMessage({
          id: 'job_create_redirect_registration',
          defaultMessage:
            'Your job was saved, you need to register to complete the job publishing.',
        }),
      });
    });
  }
};

interface JobWizardStepFormProps {
  /**
   * The job type to create.
   */
  jobType: string;
}
export const JobWizardStepForm = ({ jobType }: JobWizardStepFormProps) => {
  const intl = useIntl();
  let errorList: React.ReactNode = null;
  const { JOBS } = useFrontendConfig();
  const FIELD_GROUPS: FieldGroupInfo[] = Object.values(JOBS.JOB_DETAIL_FIELDS);
  const userContext: UserContextInterface = useContext(UserContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const appContext: AppContextInterface = useContext(AppContext);
  const { stepsInfo } = useRuntimeStepsInfo(JobWizardFlow.New);
  const { bundles } = useJobWizardContext();
  const messagesService = useMessages();

  // Get the current bundle if there is any.
  const currentBundle: Product = getCurrentBundle(
    bundles,
    pathContext.querystring,
  );

  // Get the default product bundle if there is no current bundle.
  let defaultProductBundle: Product | null = useDefaultProductBundle(
    !!currentBundle,
    appContext.locale,
  );

  // Check if the current bundle contains all available job upgrades and makes sure upgrades step
  // is skipped in this case.
  useBundleContainsAllUpgrades(
    currentBundle ?? defaultProductBundle,
    appContext.locale,
  );

  // We do not need the wizard steps here
  // we just directly push to the next step on clicking on job type
  // Check if the current step is the last based on path.
  const currentStep = stepsInfo.findIndex(
    (step) => step.page === pathContext.pathname,
  );

  // Check if step is the last one.
  const isLastStep = currentStep === stepsInfo.length - 1;

  // Adding the job. If it is a new job it will call the addJob mutation with the job data.
  const [addJob, { data, loading, error }] = useMutation(getMutation(jobType), {
    update(cache, result) {
      cache.evict({ fieldName: 'organizationJobs' });
    },
  });

  if (error || (data && !data.addJob)) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="job_create_error"
          defaultMessage="There was an error creating the job."
        />
      </MessageAlert>
    );
  }

  if (data?.addJob?.errors?.length > 0) {
    errorList = <ContentErrorList errors={data.addJob.errors} />;
  }

  // After a job is created an order will be made. This mutation takes a job id
  // and will create an order for the job.
  const [
    checkout,
    { data: checkoutData, loading: checkoutLoading, error: checkoutError },
  ] = useMutation(JOB_CHECKOUT_ORDER);

  if (checkoutError) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="job_order_create_error"
          defaultMessage="There was an error creating the order."
        />
      </MessageAlert>
    );
  }

  if (checkoutData?.jobCheckoutOrder?.errors?.length > 0) {
    errorList = (
      <ContentErrorList errors={checkoutData.jobCheckoutOrder.errors} />
    );
  }

  // Combine field errors from addJob and checkout.
  // @TODO - IF this works well we can actually remove these from the errorList and keep that
  // for only general error messages (mutation unexpected errors).
  const fieldErrorList = [
    ...(data?.addJob?.errors || []),
    ...(checkoutData?.jobCheckoutOrder?.errors || []),
  ];

  return (
    <JobWizardStepFormView
      errorList={errorList}
      fieldErrorList={fieldErrorList}
      jobType={jobType}
      submitting={loading || checkoutLoading}
      bundle={currentBundle}
      onSubmit={(formData) => {
        const job: any = getAPIEntityValues(FIELD_GROUPS, {
          ...(formData as any),
        });

        // Add the anonymous token to the job.
        job.anonymousUserToken = getAnonymousUserToken();
        // Add the current bundle ID to the job.
        job.bundle = currentBundle?.id;

        addJob({
          variables: {
            data: Object.assign(job, {
              organizationProfileId:
                userContext?.currentOrganizationProfile?.id,
            }),
          },
        }).then((response) => {
          if (response.data?.addJob?.job) {
            // Continue checkout process for non-anonymous users.
            handleJobPostingComplete(
              response,
              stepsInfo[currentStep + 1].page,
              messagesService,
              intl,
              response.data?.addJob?.job?.id,
              pathContext.querystring,
              isLastStep,
              checkout,
              userContext.user,
              appContext.locale,
              currentBundle,
            );
          }
        });
      }}
    />
  );
};
export default JobWizardStepForm;
